/**
 * Goals View Page — full goal detail with inline progress editor.
 *
 * Displays title, description, category, due date, status badge, progress bar.
 * Inline progress editor: click current % → input → save via BLL PATCH.
 */

import * as React from 'react';
import { useParams, useNavigate } from 'react-router';
import { useOne, useUpdate } from '@refinedev/core';
import {
  Button,
  Card,
  CardContent,
  Badge,
  StatusBadge,
} from '@yesbos/ui';
import { ArrowLeft, Target } from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────────────────

interface Goal {
  id: string;
  title: string;
  description?: string;
  progress: number;
  status: 'on_track' | 'at_risk' | 'behind' | 'completed' | 'draft';
  due_date?: string;
  employee_id?: string;
  employee_name?: string;
  category?: string;
  created_at?: string;
}

// ── Progress bar ──────────────────────────────────────────────────────────────

function ProgressBar({ value }: { value: number }) {
  const color = value >= 75 ? 'bg-green-500' : value >= 40 ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
      <div
        className={`h-full rounded-full transition-all ${color}`}
        style={{ width: `${value}%` }}
      />
    </div>
  );
}

// ── Detail Row ────────────────────────────────────────────────────────────────

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
      <div className="text-sm">{children}</div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function GoalsViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { mutate: update } = useUpdate();

  const { data, isLoading, refetch } = useOne<Goal>({
    resource: 'goals',
    id: id!,
  });
  const goal = data?.data as Goal | undefined;

  const [editingProgress, setEditingProgress] = React.useState(false);
  const [localProgress, setLocalProgress] = React.useState(0);
  const [saving, setSaving] = React.useState(false);
  const [toast, setToast] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (goal) setLocalProgress(goal.progress ?? 0);
  }, [goal]);

  React.useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  async function handleSaveProgress() {
    setSaving(true);
    const status: Goal['status'] =
      localProgress === 100 ? 'completed' :
      localProgress >= 70 ? 'on_track' :
      localProgress >= 40 ? 'at_risk' : 'behind';
    await new Promise<void>((resolve, reject) => {
      update(
        { resource: 'goals', id: id!, values: { progress: localProgress, status } },
        {
          onSuccess: () => { refetch(); setToast('Progress updated.'); resolve(); },
          onError: () => { setToast('Failed to save.'); reject(); },
        },
      );
    }).catch(() => {});
    setSaving(false);
    setEditingProgress(false);
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 p-6 max-w-2xl">
        <div className="h-8 w-48 rounded-md bg-muted animate-pulse" />
        <div className="h-56 rounded-lg border bg-muted animate-pulse" />
      </div>
    );
  }

  if (!goal) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Goal not found.</p>
        <Button className="mt-4" variant="outline" onClick={() => navigate('/goals')}>
          Back to Goals
        </Button>
      </div>
    );
  }

  const daysUntilDue = goal.due_date
    ? Math.ceil((new Date(goal.due_date).getTime() - Date.now()) / 86_400_000)
    : null;

  return (
    <div className="flex flex-col gap-6 p-6 max-w-2xl">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 rounded-md px-4 py-2 text-sm text-white bg-green-600 shadow-lg">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="icon" className="mt-0.5" onClick={() => navigate('/goals')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h1 className="text-2xl font-semibold leading-tight">{goal.title}</h1>
            <StatusBadge status={goal.status} />
          </div>
          {goal.employee_name && (
            <p className="text-sm text-muted-foreground mt-0.5">{goal.employee_name}</p>
          )}
        </div>
      </div>

      {/* Progress card */}
      <Card>
        <CardContent className="pt-5 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Progress</p>
            {editingProgress ? (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={localProgress}
                  onChange={(e) => setLocalProgress(Math.min(100, Math.max(0, Number(e.target.value))))}
                  className="w-16 border rounded px-2 py-1 text-sm text-center focus:outline-none focus:ring-1 focus:ring-primary"
                  autoFocus
                />
                <span className="text-sm text-muted-foreground">%</span>
                <Button size="sm" onClick={handleSaveProgress} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setEditingProgress(false); setLocalProgress(goal.progress); }}>
                  Cancel
                </Button>
              </div>
            ) : (
              <button
                onClick={() => setEditingProgress(true)}
                className="text-sm font-semibold hover:text-primary transition-colors"
              >
                {goal.progress}%
              </button>
            )}
          </div>
          <ProgressBar value={goal.progress} />
        </CardContent>
      </Card>

      {/* Details */}
      <Card>
        <CardContent className="pt-5 grid gap-4 sm:grid-cols-2">
          {goal.description && (
            <div className="sm:col-span-2">
              <DetailRow label="Description">
                <p className="text-sm text-foreground">{goal.description}</p>
              </DetailRow>
            </div>
          )}
          {goal.category && (
            <DetailRow label="Category">
              <Badge variant="outline">{goal.category}</Badge>
            </DetailRow>
          )}
          {goal.due_date && (
            <DetailRow label="Due Date">
              <span>
                {new Date(goal.due_date).toLocaleDateString('en-IN', {
                  day: 'numeric', month: 'short', year: 'numeric',
                })}
                {daysUntilDue !== null && (
                  <span className={`ml-1.5 text-xs ${daysUntilDue < 0 ? 'text-destructive' : daysUntilDue <= 7 ? 'text-yellow-600' : 'text-muted-foreground'}`}>
                    {daysUntilDue < 0
                      ? `${Math.abs(daysUntilDue)} days overdue`
                      : daysUntilDue === 0
                      ? 'Due today'
                      : `${daysUntilDue} days left`}
                  </span>
                )}
              </span>
            </DetailRow>
          )}
          {goal.created_at && (
            <DetailRow label="Created">
              {new Date(goal.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </DetailRow>
          )}
        </CardContent>
      </Card>

      {/* Edit button */}
      <div className="flex gap-3">
        <Button variant="outline" onClick={() => navigate(`/goals/${id}/edit`)}>
          Edit Goal
        </Button>
      </div>
    </div>
  );
}

export default GoalsViewPage;
